import { RacemapColors } from '@racemap/utilities/consts/common';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import {
  labelFormatDistance,
  labelFormatElevation,
  labelFormatVelocity,
} from '@racemap/utilities/formattings/units';
import { parseDateTimeSafe } from '@racemap/utilities/functions/datetime';
import {
  distanceUnitByUnitType,
  elevationUnitByUnitType,
  unitAwareDistance,
  unitAwareVelocity,
  velocityUnitByUnitType,
} from '@racemap/utilities/functions/units';
import type { Shadowtrack } from '@racemap/utilities/types/events';
import type { SplitObject } from '@racemap/utilities/types/geos';
import type {
  AcceptedReadRust,
  PredictionDebugData,
  RejectedReadRust,
} from '@racemap/utilities/types/trackping';
import { ReadVariants, type Reader } from '@racemap/utilities/types/types';
import type { UnitType } from '@racemap/utilities/types/units';
import type {
  DefaultLabelFormatterCallbackParams,
  TooltipComponentFormatterCallbackParams,
} from 'echarts';
import ReactECharts from 'echarts-for-react';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { type FC, memo, useMemo, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Timestamp } from 'src/components/BasicComponents/Timestamp/Timestamp';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import { Switch } from '../../BasicComponents/Switch/Switch';
import { getCircleSVGBase64 } from '../../Charts/chartSVGs';
import { SHADOW_TRACK_STEP, calculateVelocityInKMH, colorTable } from './utils';

interface Props {
  starterDebugData: PredictionDebugData | null;
  currentEvent: Immutable<CurrentEvent>;
  chunkedTrack: Immutable<Shadowtrack>;
  splits: Array<SplitObject>;
  trackLength: number;
  unitType: UnitType;
  onReadInFocus: (reader: Reader | AcceptedReadRust | null) => void;
}

enum SeriesNames {
  AcceptedReads = 'Accepted',
  RejectedReads = 'Rejected',
  UnusedReads = 'Unused',
  ReplayInterpolation = 'Replay',
  LiveIdeally = 'Live Ideally',
  LiveExtrapolation = 'Live',
  Elevation = 'Elevation',
  LiveSpeed = 'Live speeds',
  ReplaySpeed = 'Replay speeds',
  Splits = 'Splits',
}

const defaultSelectedSeries = {
  'Live speeds': false,
  'Replay speeds': false,
  'Unused': false,
  'Live Ideally': false,
};

const MovesChartRaw: FC<Props> = ({
  starterDebugData,
  currentEvent,
  chunkedTrack,
  trackLength,
  splits,
  unitType,
  onReadInFocus,
}) => {
  // the other roption is by location
  const [baseIsByTime, setBaseIsByTime] = useState(true);

  const [selectedSeries, setSelectedSeries] = useState<Record<string, boolean>>({
    ...defaultSelectedSeries,
  });

  const chartOptions = useMemo(() => {
    // Prepare the base64 SVG for acceptedReads symbol
    const acceptedReadSymbol = getCircleSVGBase64(
      RacemapColors.PaleBlue,
      RacemapColors.DarkBlue,
      16,
      7,
    );
    const rejectedReadSymbol = getCircleSVGBase64(
      RacemapColors.LightRed,
      RacemapColors.DarkRed,
      16,
      7,
    );
    const unusedReadSymbol = getCircleSVGBase64('#fff', RacemapColors.AltRed, 16, 7);

    const liveSpeeds = starterDebugData
      ? starterDebugData.extrapolatedPointsLive
          .slice(1)
          .map((point, i) => [
            baseIsByTime ? Date.parse(point.timestamp) : point.offset,
            unitAwareVelocity(
              calculateVelocityInKMH(
                starterDebugData.extrapolatedPointsLive[i],
                starterDebugData.extrapolatedPointsLive[i + 1],
              ),
              unitType,
            ),
          ])
      : [];

    const replaySpeeds = starterDebugData
      ? starterDebugData.interpolatedPointsReplay
          .slice(1)
          .map((point, i) => [
            baseIsByTime ? Date.parse(point.timestamp) : point.offset,
            unitAwareVelocity(
              calculateVelocityInKMH(
                starterDebugData.interpolatedPointsReplay[i],
                starterDebugData.interpolatedPointsReplay[i + 1],
              ),
              unitType,
            ),
          ])
      : [];

    const maxSpeed = [...liveSpeeds, ...replaySpeeds]
      .map(([_, speed]) => speed)
      .reduce((r, a) => Math.max(r, a), 0);

    const elevationData = chunkedTrack.geometry.coordinates.map(([_1, _2, elv], i) => [
      unitAwareDistance(i * SHADOW_TRACK_STEP, unitType),
      unitAwareDistance(elv, unitType),
    ]);

    const maxElevation = elevationData.reduce((r, [_, a]) => Math.max(r, a), 0);

    const acceptedReads = starterDebugData ? starterDebugData.acceptedReads : [];
    const rejectedReads = starterDebugData ? starterDebugData.rejectedReads : [];
    const unusedReads = rejectedReads.filter((rJ) => rJ.variant === ReadVariants.Unused);

    const extrapolatedPointsLive = starterDebugData ? starterDebugData.extrapolatedPointsLive : [];
    const extrapolatedPointsIdeally = starterDebugData
      ? starterDebugData.extrapolatedPointsIdeally
      : [];
    const interpolatedPointsReplay = starterDebugData
      ? starterDebugData.interpolatedPointsReplay
      : [];
    const eventStartTime = Date.parse(currentEvent.startTime);
    const eventEndTime = Date.parse(currentEvent.endTime);
    const predLiveStartTime = acceptedReads[1]?.receivedAt;

    const totalTime = eventEndTime - eventStartTime;
    const speedSeriesSelected = selectedSeries['Live speeds'] || selectedSeries['Replay speeds'];

    function filterByTimeOrOffset(
      acceptedRead: AcceptedReadRust | RejectedReadRust,
      timeOrOffset: number,
    ) {
      return baseIsByTime
        ? Date.parse(acceptedRead.timestamp) > timeOrOffset - 5 * OneMinuteInMillis &&
            Date.parse(acceptedRead.timestamp) < timeOrOffset + 5 * OneMinuteInMillis
        : acceptedRead.offset > timeOrOffset - 200 && acceptedRead.offset < timeOrOffset + 200;
    }

    const timeBasedMarkerLines = [
      {
        name: 'Start of Event',
        xAxis: eventStartTime,
        label: {
          position: 'insideEndBottom',
          formatter: ({ name }: { name: string }) => name,
        },
        lineStyle: {
          color: 'lightgrey',
        },
      },
      {
        name: 'End of Event',
        xAxis: eventEndTime,
        label: {
          position: 'insideEndTop',
          formatter: ({ name }: { name: string }) => name,
        },
        lineStyle: {
          color: 'lightgrey',
        },
      },
    ];
    if (predLiveStartTime) {
      timeBasedMarkerLines.push({
        name: 'Start Live Prediction',
        xAxis: Date.parse(predLiveStartTime),
        label: {
          position: 'insideEndTop',
          formatter: ({ name }: { name: string }) => name,
        },
        lineStyle: {
          color: 'violet',
        },
      });
    }

    const offsetBasedMarkerLines = [
      {
        name: 'Start',
        xAxis: 0,
        label: {
          position: 'insideEndBottom',
          formatter: ({ name }: { name: string }) => name,
        },
        lineStyle: {
          color: 'lightgrey',
        },
      },
      {
        name: 'Finish',
        xAxis: trackLength,
        label: {
          position: 'insideEndTop',
          formatter: ({ name }: { name: string }) => name,
        },
        lineStyle: {
          color: 'lightgrey',
        },
      },
    ];

    return {
      grid: {
        left: 76,
        right: 56,
        top: 36,
        bottom: 64,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          animation: false,
        },
        formatter: (params: TooltipComponentFormatterCallbackParams) => {
          if (!Array.isArray(params) || !Array.isArray(params[0].value)) return '';
          // only render if one of line charts is hovered
          if (params[0].componentSubType === 'scatter') return '';

          const timeOrOffset = params[0].value[0] as number;

          return ReactDOMServer.renderToString(
            <MovesChartTooltip
              components={params}
              acceptedReads={acceptedReads.filter((aR) => filterByTimeOrOffset(aR, timeOrOffset))}
              rejectedReads={rejectedReads.filter((rR) => filterByTimeOrOffset(rR, timeOrOffset))}
              baseIsByTime={baseIsByTime}
              unitType={unitType}
            />,
          );
        },
      },
      legend: {
        selected: selectedSeries,
        itemWidth: 14,
        itemHeight: 14,
        data: [
          {
            name: SeriesNames.AcceptedReads,
            icon: acceptedReadSymbol,
          },
          {
            name: SeriesNames.RejectedReads,
            icon: rejectedReadSymbol,
          },
          {
            name: SeriesNames.UnusedReads,
            icon: unusedReadSymbol,
          },
          SeriesNames.ReplayInterpolation,
          SeriesNames.LiveExtrapolation,
          SeriesNames.LiveIdeally,
          SeriesNames.Elevation,
          SeriesNames.LiveSpeed,
          SeriesNames.ReplaySpeed,
          SeriesNames.Splits,
        ],
        tooltip: {
          show: true,
          formatter: (params: { name: string }) => {
            const legendHints: Record<string, string> = {
              [SeriesNames.LiveExtrapolation]:
                'Shows the live position based upon Racemaps prediction algorithm.',
              [SeriesNames.LiveIdeally]:
                'Shows the live position of the starter based on ideal live data. No latency considered.',
              [SeriesNames.AcceptedReads]:
                'All reads that were considered to be valid for the prediction.',
              [SeriesNames.RejectedReads]:
                'Reads that were rejected from the prediction. (Too fast or too slow etc.)',
              [SeriesNames.UnusedReads]: 'Reads that were not used in the prediction at all.',
              [SeriesNames.ReplayInterpolation]:
                'Shows the position based on all reads in a replay fashion (no prediction - after the race).',
              [SeriesNames.ReplaySpeed]: 'Speed calculated between each point in the replay data.',
              [SeriesNames.LiveSpeed]:
                'Speed calculated between each point in the live predicted data.',
              [SeriesNames.Elevation]: 'Elevation data for the starter over distance.',
              [SeriesNames.Splits]: 'Splits/Intermediate user defined marks on the track.',
            };
            return legendHints[params.name] || params.name;
          },
        },
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          filterMode: 'none',
        },
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
        },
      ],
      xAxis: {
        type: baseIsByTime ? 'time' : 'value',
        id: baseIsByTime ? 'time' : 'offset',
        min: baseIsByTime ? eventStartTime - totalTime * 0.05 : 0,
        max: baseIsByTime
          ? eventEndTime + totalTime * 0.05
          : unitAwareDistance(trackLength, unitType),
        name: baseIsByTime ? 'Time' : `Distance in ${distanceUnitByUnitType(unitType, false)}s`,
        nameGap: 30,
        nameLocation: 'middle',
        axisLabel: {
          formatter: (value: number) => {
            return baseIsByTime
              ? parseDateTimeSafe(value)?.toLocaleString(DateTime.TIME_24_WITH_SECONDS)
              : labelFormatDistance(value, unitType, 1, false);
          },
        },
        axisPointer: {
          label: {
            formatter: ({ value }: { value: number }) => {
              return baseIsByTime
                ? parseDateTimeSafe(value)?.toUTC().toISO() || 'Invalid Date'
                : labelFormatDistance(value, unitType, 1, false);
            },
          },
        },
      },
      animation: false,
      yAxis: [
        {
          type: baseIsByTime ? 'value' : 'time',
          id: baseIsByTime ? 'offset' : 'time',
          name: baseIsByTime ? `Distance in ${distanceUnitByUnitType(unitType, false)}s` : 'Time',
          nameLocation: 'middle',
          min: baseIsByTime ? 0 : eventStartTime - totalTime * 0.05,
          max: baseIsByTime
            ? Math.round(unitAwareDistance(trackLength, unitType) * 1.1)
            : eventEndTime + totalTime * 0.05,
          nameGap: 70,
          axisLabel: {
            formatter: (value: number) => {
              return baseIsByTime
                ? labelFormatDistance(value, unitType, 1, false)
                : parseDateTimeSafe(value)?.toLocaleString(DateTime.TIME_24_WITH_SECONDS);
            },
          },
          axisPointer: {
            label: {
              formatter: ({ value }: { value: number }) => {
                return baseIsByTime
                  ? labelFormatDistance(value, unitType)
                  : parseDateTimeSafe(value)?.toUTC().toISO() || 'Invalid Date';
              },
            },
          },
        },
        {
          type: 'value',
          id: 'elevation',
          name: `Elevation in ${elevationUnitByUnitType(unitType)}`,
          nameLocation: 'middle',
          min: 0,
          max: Math.round(maxElevation * 1.1),
          nameGap: 70,
          axisLabel: {
            formatter: (value: number) => `${value.toFixed(0)}`,
          },
          axisPointer: {
            label: {
              formatter: ({ value }: { value: number }) => {
                return labelFormatElevation(value, unitType);
              },
            },
          },
        },
        {
          type: 'value',
          id: 'speed',
          name: `Speed in ${velocityUnitByUnitType(unitType)}`,
          nameLocation: 'middle',
          show: speedSeriesSelected,
          nameGap: 70,
          min: 0,
          max: Math.round(maxSpeed * 1.1),
          axisLabel: {
            formatter: (value: number) => labelFormatVelocity(value, unitType, 1, false),
          },
          axisPointer: {
            show: speedSeriesSelected,
            label: {
              formatter: ({ value }: { value: number }) => {
                return labelFormatVelocity(value, unitType);
              },
            },
          },
        },
      ],
      series: [
        {
          type: 'scatter',
          id: 'acceptedReads',
          name: SeriesNames.AcceptedReads,
          color: colorTable.acceptedReadColor,
          data: acceptedReads.map((read) =>
            baseIsByTime
              ? [Date.parse(read.timestamp), unitAwareDistance(read.offset, unitType)]
              : [unitAwareDistance(read.offset, unitType), Date.parse(read.timestamp)],
          ),
          symbol: acceptedReadSymbol,
          symbolSize: 10,
          markLine: {
            symbol: ['none', 'none'],
            data: [
              {
                yAxis: unitAwareDistance(trackLength, unitType),
                symbol: 'none',
                lineStyle: { type: 'dashed' },
                label: { show: false },
                name: 'Track Finish',
              },
              ...acceptedReads
                .filter(
                  (read) =>
                    Math.abs(Date.parse(read.timestamp) - Date.parse(read.receivedAt)) >
                    0.01 * (eventEndTime - eventStartTime),
                )
                .map((read) => [
                  {
                    coord: baseIsByTime
                      ? [Date.parse(read.timestamp), unitAwareDistance(read.offset, unitType)]
                      : [unitAwareDistance(read.offset, unitType), Date.parse(read.timestamp)],
                    symbol: 'none',
                  },
                  {
                    coord: baseIsByTime
                      ? [Date.parse(read.receivedAt), unitAwareDistance(read.offset, unitType)]
                      : [unitAwareDistance(read.offset, unitType), Date.parse(read.receivedAt)],
                    symbol: 'emptyCircle',
                  },
                ]),
            ],
          },
        },
        {
          type: 'line',
          id: 'splits',
          name: SeriesNames.Splits,
          color: 'lightgrey',
          data: [],
          markLine: {
            symbol: ['none', 'none'],
            data: splits
              .filter((s) => s.properties.offset != null)
              .map((split) => ({
                yAxis: unitAwareDistance(split.properties.offset || 0, unitType),
                lineStyle: { type: 'dashed', color: 'lightgrey' },
                name: split.properties.name,
                label: {
                  position: 'insideStartTop',
                  formatter: ({ name }: { name: string }) => name,
                },
              })),
          },
          showSymbol: false,
        },
        {
          type: 'scatter',
          id: 'rejectedReads',
          name: SeriesNames.RejectedReads,
          color: colorTable.rejectedReadColor,
          symbol: rejectedReadSymbol,
          symbolSize: 10,
          data: rejectedReads.map((rejectedRead) =>
            baseIsByTime
              ? [
                  Date.parse(rejectedRead.timestamp),
                  unitAwareDistance(rejectedRead.offset, unitType) || 0,
                ]
              : [
                  unitAwareDistance(rejectedRead.offset, unitType) || 0,
                  Date.parse(rejectedRead.timestamp),
                ],
          ),
        },
        {
          type: 'scatter',
          id: 'unusedRead',
          name: SeriesNames.UnusedReads,
          color: colorTable.unusedReadColor,
          symbol: unusedReadSymbol,
          symbolSize: 6,
          data: unusedReads.map((unusedRead) =>
            baseIsByTime
              ? [
                  Date.parse(unusedRead.timestamp),
                  unitAwareDistance(unusedRead.offset, unitType) || 0,
                ]
              : [
                  unitAwareDistance(unusedRead.offset, unitType) || 0,
                  Date.parse(unusedRead.timestamp),
                ],
          ),
        },
        {
          type: 'line',
          id: 'liveOffset',
          name: SeriesNames.LiveExtrapolation,
          color: colorTable.liveColor,
          data: extrapolatedPointsLive.map((point) =>
            baseIsByTime
              ? [Date.parse(point.timestamp), unitAwareDistance(point.offset, unitType)]
              : [unitAwareDistance(point.offset, unitType), Date.parse(point.timestamp)],
          ),
          showSymbol: false,
          markLine: {
            symbol: ['none', 'none'],
            data: baseIsByTime ? timeBasedMarkerLines : offsetBasedMarkerLines,
          },
        },
        {
          type: 'line',
          id: 'liveIdeallyOffset',
          name: SeriesNames.LiveIdeally,
          color: colorTable.liveIdeallyColor,
          data: extrapolatedPointsIdeally.map((point) =>
            baseIsByTime
              ? [Date.parse(point.timestamp), unitAwareDistance(point.offset, unitType)]
              : [unitAwareDistance(point.offset, unitType), Date.parse(point.timestamp)],
          ),
          showSymbol: false,
          markLine: {
            symbol: ['none', 'none'],
            data: baseIsByTime ? timeBasedMarkerLines : offsetBasedMarkerLines,
          },
        },
        {
          type: 'line',
          id: 'replayOffset',
          name: SeriesNames.ReplayInterpolation,
          color: colorTable.replayColor,
          data: interpolatedPointsReplay.map((point) =>
            baseIsByTime
              ? [Date.parse(point.timestamp), unitAwareDistance(point.offset, unitType)]
              : [unitAwareDistance(point.offset, unitType), Date.parse(point.timestamp)],
          ),
          showSymbol: false,
        },
        !baseIsByTime && {
          type: 'line',
          id: 'elevation-data',
          name: SeriesNames.Elevation,
          color: colorTable.elevationColor,
          symbol: 'diamond',
          data: elevationData,
          yAxisIndex: 1,
          showSymbol: false,
          tooltip: {
            valueFormatter: (value: number) => labelFormatElevation(value, unitType),
          },
        },
        {
          type: 'line',
          id: 'liveSpeed',
          name: SeriesNames.LiveSpeed,
          yAxisIndex: 2,
          color: colorTable.liveSpeedColor,
          data: liveSpeeds,
          showSymbol: false,
        },
        {
          type: 'line',
          id: 'replaySpeed',
          name: SeriesNames.ReplaySpeed,
          yAxisIndex: 2,
          color: colorTable.replaySpeedColor,
          data: replaySpeeds,
          showSymbol: false,
        },
      ],
    };
  }, [starterDebugData, selectedSeries, baseIsByTime]);

  return (
    <div>
      <Switch
        value={!baseIsByTime}
        onChange={() => {
          setBaseIsByTime(!baseIsByTime);
        }}
        label="Swap axes"
      />
      <ReactECharts
        option={chartOptions}
        style={{ height: '450px', width: '100%' }}
        onEvents={{
          legendselectchanged: (evt: { selected: Record<string, boolean> }) => {
            setSelectedSeries(evt.selected);
          },
          mouseout: () => {
            onReadInFocus(null);
          },
          mousemove: (evt: DefaultLabelFormatterCallbackParams) => {
            if (evt.seriesId !== 'acceptedReads' && evt.seriesId !== 'rejectedReads') return;
            if (!Array.isArray(evt.data)) return;
            let reader = null;
            if (starterDebugData != null) {
              if (evt.seriesId === 'acceptedReads') {
                reader = starterDebugData.acceptedReads[evt.dataIndex];
              } else {
                reader = starterDebugData.rejectedReads[evt.dataIndex];
              }
            }
            if (reader == null) {
              onReadInFocus(null);
              return;
            }
            onReadInFocus(reader);
          },
        }}
      />
    </div>
  );
};

export const MovesChart = memo(MovesChartRaw);

interface MovesChartTooltipProps {
  components: TooltipComponentFormatterCallbackParams;
  acceptedReads: Array<AcceptedReadRust>;
  rejectedReads: Array<RejectedReadRust>;
  baseIsByTime: boolean;
  unitType: UnitType;
}

export const MovesChartTooltip: FC<MovesChartTooltipProps> = ({
  components,
  acceptedReads,
  rejectedReads,
  baseIsByTime,
  unitType,
}) => {
  if (!Array.isArray(components) || !Array.isArray(components[0].value)) return <></>;
  const time = components[0].value[0] as number;

  // necessary to use inline styles because the components get rendered to strings and
  // emotion dont inject the created classes
  // https://github.com/emotion-js/emotion/issues/2906
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: 280 }}>
      <div>
        <b>Time:</b> <Timestamp time={time} format="time" />
      </div>
      {components.map((component, i) => {
        if (!Array.isArray(component.value)) return <></>;
        return (
          <SeriesValue
            key={component.seriesId || i}
            color={component.color?.toString() || '#fff'}
            name={component.seriesName || ''}
            unitType={unitType}
            value={(baseIsByTime ? component.value[1] : component.value[0]) as number}
            seriesId={component.seriesId || ''}
          />
        );
      })}

      {acceptedReads.length > 0 && (
        <ReadsList reads={acceptedReads} label="Accepted Reads" unitType={unitType} />
      )}
      {rejectedReads.length > 0 && (
        <ReadsList reads={rejectedReads} label="Rejected Reads" unitType={unitType} />
      )}
    </div>
  );
};

const SeriesValue: FC<{
  color: string;
  name: string;
  value: number | null;
  unitType: UnitType;
  seriesId: string;
}> = ({ color, name, value, unitType, seriesId }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span
        style={{
          display: 'inline-block',
          marginRight: '6px',
          borderRadius: '10px',
          width: '10px',
          height: '10px',
          backgroundColor: color,
        }}
      />
      <div>{name}</div>
    </div>
    <div style={{ fontWeight: 500, minWidth: 100, textAlign: 'end' }}>
      {seriesId.includes('Speed')
        ? labelFormatVelocity(value || -1, unitType)
        : labelFormatDistance(value || -1, unitType)}
    </div>
  </div>
);

const ReadsList: FC<{
  reads: Array<AcceptedReadRust | RejectedReadRust>;
  label: string;
  unitType: UnitType;
}> = ({ reads, label, unitType }) => {
  return (
    <div>
      <hr style={{ width: '95%' }} />
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>

      {reads.map((read) => {
        return (
          <div
            key={`${read.readerId}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <div style={{ fontWeight: 500 }}>{read.readerId}</div>
            <Timestamp time={read.timestamp} format="longTime" />
            <div style={{ fontWeight: 500 }}>
              {labelFormatDistance(unitAwareDistance(read.offset, unitType), unitType)}
            </div>
          </div>
        );
      })}
    </div>
  );
};
